import { cn } from "@/lib/cn";
import type { AvailabilityColor } from "@/types/entities";

/** Dot color + human label for each backend availability code. */
const AVAILABILITY_DOT: Record<AvailabilityColor, { className: string; label: string }> = {
  RED: { className: "bg-red-500", label: "Red" },
  BLUE: { className: "bg-blue-500", label: "Blue" },
  BLACK: { className: "bg-ink-900 dark:bg-bridge-fg", label: "Black" },
};

interface AvailabilityDotProps {
  color: AvailabilityColor | string | null | undefined;
  /** Diameter in pixels. */
  size?: number;
  className?: string;
}

/**
 * Traffic-light style availability indicator. Renders nothing when the backend
 * sends no color (null / unknown), so callers can drop it inline unconditionally.
 */
export function AvailabilityDot({ color, size = 10, className }: AvailabilityDotProps) {
  const key = typeof color === "string" ? (color.toUpperCase() as AvailabilityColor) : null;
  const spec = key ? AVAILABILITY_DOT[key] : undefined;
  if (!spec) return null;
  return (
    <span
      className={cn("inline-block shrink-0 rounded-full", spec.className, className)}
      style={{ width: size, height: size }}
      role="img"
      aria-label={`Availability: ${spec.label}`}
      title={`Availability: ${spec.label}`}
    />
  );
}
